import { useEffect, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";

const TYPE_COLORS = {
  deposit: "#8AE234",
  withdrawal: "#EF4444",
  daily_profit: "#00E5FF",
  referral_bonus: "#A855F7",
  team_income: "#3B82F6",
  bot_activation: "#F59E0B",
  leadership_bonus: "#EAB308",
  rank_upgrade: "#94A3B8",
  shareholder_commission: "#EC4899",
  credit_hold_profit: "#F59E0B",
  credit_repayment: "#94A3B8",
  credit_hold_release: "#8AE234",
};

// Feb-19: named groupings so Dashboard cards can deep-link to filtered ledger views
const GROUPS = {
  profit: ["daily_profit", "credit_hold_profit", "credit_hold_release", "rank_bonus"],
  network: ["referral_bonus", "team_income", "leadership_bonus", "shareholder_commission", "shareholder_bonus"],
  credit: ["credit_hold_profit", "credit_repayment", "credit_hold_release"],
};

export default function Transactions() {
  const [items, setItems] = useState([]);
  const [params] = useSearchParams();
  const initialFilter = params.get("filter") || "all";
  const [filter, setFilter] = useState(initialFilter);
  useEffect(() => { (async () => { const { data } = await api.get("/transactions/my"); setItems(data.transactions); })(); }, []);

  const filtered = filter === "all"
    ? items
    : (GROUPS[filter] ? items.filter(t => GROUPS[filter].includes(t.type)) : items.filter(t => t.type === filter));
  const chips = ["all", "profit", "network", "credit", ...Array.from(new Set(items.map(i => i.type)))];

  return (
    <AppLayout variant="user">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-nb-cyan">Ledger</div>
        <h1 className="font-heading font-bold text-3xl md:text-4xl text-white mt-1">Transactions</h1>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {chips.map((t) => (
          <button key={t} data-testid={`filter-${t}`} onClick={()=>setFilter(t)} className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-widest border transition-colors ${filter===t?"border-[#8AE234] text-[#8AE234] bg-[#8AE234]/10":"border-white/10 text-slate-400 hover:text-white"}`}>{t.replace("_"," ")}</button>
        ))}
      </div>

      <div className="nb-card p-0 overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-[#070B14] text-xs uppercase text-slate-500 tracking-widest">
            <tr>
              <th className="text-left px-4 py-3 whitespace-nowrap">Type</th>
              <th className="text-left px-4 py-3">Details</th>
              <th className="text-right px-4 py-3 whitespace-nowrap">Amount</th>
              <th className="text-right px-4 py-3 whitespace-nowrap">When</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {filtered.length === 0 ? (
              <tr><td colSpan={4} className="text-center py-8 text-slate-500">No transactions</td></tr>
            ) : filtered.map((t, i) => (
              <tr key={i} data-testid={`tx-row-${i}`}>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className="text-xs uppercase tracking-widest font-semibold" style={{color: TYPE_COLORS[t.type]||"#94A3B8"}}>{t.type.replace("_"," ")}</span>
                </td>
                <td className="px-4 py-3 text-slate-300 text-xs font-mono max-w-[420px] break-all">
                  {t.meta ? Object.entries(t.meta).map(([k,v])=>`${k}:${typeof v === "number" ? v.toFixed?.(2) ?? v : v}`).join(" · ") : "—"}
                </td>
                <td className={`px-4 py-3 text-right font-mono whitespace-nowrap ${t.amount>=0?"text-[#8AE234]":"text-red-400"}`}>{t.amount>=0?"+":""}${Math.abs(t.amount).toFixed(4)}</td>
                <td className="px-4 py-3 text-right text-xs text-slate-500 whitespace-nowrap">{new Date(t.created_at).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AppLayout>
  );
}
